import { createApp } from 'vue';
import App from './App.vue';
import store from './store';
import { setupSentry } from './sentry';
import router from './router';
import { initUpdateCheck } from './utils/checkForUpdate';
import { flushQueuedBugReports } from './utils/bugReports';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './assets/scss/main.scss';
import { watchViewport } from './utils/viewport';
import { trackUsage } from './utils/usage.js';
import './utils/screenPin.js'; // keeps the bug button at the real bottom of the screen (iOS cuts the page short)
import { installPadNav } from './utils/padNav.js';

// One anonymous ping per open → Bug Desk's Visitors tab (house module).
trackUsage('space-base');

// Before the first paint: how much of the screen the layout actually
// reaches. See utils/viewport - this is the dead-band seatbelt.
watchViewport();

const app = createApp(App);

// Crash reporting → Sentry → Bug Desk (src/sentry.js). Production builds only.
setupSentry(app);

store.dispatch('initAuth');
store.dispatch('loadLocalGame');
// Passed the store so the auto-reload can tell a live game from a saved one
// sitting on the landing page - see utils/appUpdate.js's isSafeMomentForReload
// for why reloading mid-game is the one thing this must never do.
initUpdateCheck(store);

// Matt reported this directly: "We should allow bug reports even when
// we're offline... and then when you get back online, the next time
// they should all get reported." bugReports.js's own submitBugReport
// already queues to localStorage on failure - this is the other half,
// flushing whatever's still queued both right away (in case the app
// itself just came back online after being closed) and the instant a
// live 'online' event fires (rather than waiting for the next bug
// report to be filed before anything queued gets sent).
flushQueuedBugReports();
window.addEventListener('online', flushQueuedBugReports);

// Precaches the app shell/JS/CSS/card-art/manifest/icons (see vite.config.js's
// own workbox comment for exactly what and why) so local hotseat/vs-AI play
// keeps working with zero connectivity after one online visit. Only a real
// production build actually emits dist/service-worker.js at all (vite-plugin-pwa
// only generates the worker on `vite build`) - `yarn
// serve` has no such file, and registering against a 404 would only spam the
// console for no benefit, so this is gated the same way.
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register(`${process.env.BASE_URL}service-worker.js`).catch((err) => {
      // Best-effort, same philosophy as localGameStorage.js's own
      // try/catch - offline play still works via whatever was precached on
      // a PRIOR successful registration; a failed registration attempt
      // isn't worth surfacing to the player mid-game. A warning, not an
      // error: Sentry captures console.error, and privacy extensions that
      // refuse service workers made this its noisiest harmless report.
      // eslint-disable-next-line no-console
      console.warn('Service worker registration failed:', err);
    });
  });
}

app.use(store);
app.use(router);

app.mount('#app');

// A game controller moves a highlight round the screen and presses things
// (Steam Deck, 2026-11-02). Inert until a pad is connected.
installPadNav();
